import React, { useEffect, useId, useState } from "react";
import {
  Badge,
  Box,
  IconButton,
  LinearProgress,
  Paper,
  Popover,
  Tooltip,
  Typography,
  Chip,
} from "@mui/material";
import {
  Close,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  Cancel,
  Autorenew,
  PendingActions,
} from "@mui/icons-material";
import { keyframes } from "@mui/material/styles";
import { useAIRequests } from "../context/AIRequestContext";
import { useApp } from "../context/AppContext";
import { useNavigate } from "react-router-dom";

const spinKeyframe = keyframes({
  "0%": { transform: "rotate(0deg)" },
  "100%": { transform: "rotate(360deg)" },
});

/**
 * Maps request status to a small icon for the list row.
 * @param {string} status - Request status string.
 * @returns {JSX.Element} Icon node.
 */
function getStatusIcon(status) {
  switch (status) {
    case "completed":
      return <CheckCircle sx={{ color: "success.main" }} />;
    case "failed":
      return <ErrorIcon sx={{ color: "error.main" }} />;
    case "processing":
      return (
        <Autorenew
          sx={{
            color: "primary.main",
            animation: `${spinKeyframe} 2s linear infinite`,
          }}
        />
      );
    case "pending":
      return <HourglassEmpty sx={{ color: "warning.main" }} />;
    default:
      return <HourglassEmpty />;
  }
}

/**
 * Maps request status to MUI Chip color.
 * @param {string} status - Request status string.
 * @returns {string} Chip color prop.
 */
function getStatusColor(status) {
  switch (status) {
    case "completed":
      return "success";
    case "failed":
      return "error";
    case "processing":
      return "primary";
    case "pending":
      return "warning";
    default:
      return "default";
  }
}

/**
 * Human-readable label for the AI request type.
 * @param {string} type - Request type key.
 * @returns {string} Display label.
 */
function getTypeLabel(type) {
  if (type === "document") return "Document Generation";
  if (type === "requirement_extraction") return "Requirement Extraction";
  if (type === "session") return "Session Processing";
  if (type === "session_insights") return "Session Insights";
  if (type === "template_import") return "PowerPoint Template Import";
  return "AI Processing";
}

/**
 * Custom progress copy for completed rows. Matches the language the user sees per job type.
 * @param {object} request - AI request row.
 * @returns {string|null} Message string, or null to keep existing message.
 */
function getCompletedMessage(request) {
  if (request.status !== "completed") return null;
  if (request.type === "template_import" && request.result_data?.templateId) {
    return "Done and ready for review";
  }
  return request.progress_message || null;
}

/**
 * Navbar control: badge for active job count, popover with the full request list.
 * Reserves a fixed toolbar slot when idle so siblings do not shift when jobs appear or clear.
 * @returns {JSX.Element} Trigger slot (hidden when idle) and conditional popover.
 */
function AIRequestNotifications() {
  const { activeRequests, completedRequests, dismissRequest, cancelRequest } =
    useAIRequests();
  const { actions } = useApp();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuId = useId();
  const open = Boolean(anchorEl);
  // Reloading projects when a job finishes is done by AIJobCompletionWatcher (always mounted
  // in App.js), so it still happens while this top-bar control is hidden in focus mode.

  const allRequests = [...activeRequests, ...completedRequests];
  const hasJobs = allRequests.length > 0;
  const activeCount = activeRequests.length;

  useEffect(() => {
    if (!hasJobs) {
      setAnchorEl(null);
    }
  }, [hasJobs]);

  /**
   * Dismiss a completed or failed request from the list.
   * @param {string} requestId - Request id.
   * @param {React.MouseEvent} e - Click event.
   * @returns {Promise<void>}
   */
  const handleDismiss = async (requestId, e) => {
    e.stopPropagation();
    await dismissRequest(requestId);
  };

  /**
   * Cancel a pending or processing request.
   * @param {string} requestId - Request id.
   * @param {React.MouseEvent} e - Click event.
   * @returns {Promise<void>}
   */
  const handleCancel = async (requestId, e) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to cancel this request?")) {
      await cancelRequest(requestId);
    }
  };

  /**
   * Navigate to the relevant project or template when activating a row.
   * @param {object} request - The AI request object.
   * @returns {Promise<void>}
   */
  const handleNavigateToProject = async (request) => {
    // Template imports: open the new draft template directly in the rich editor.
    if (
      request.type === "template_import" &&
      request.status === "completed" &&
      request.result_data?.templateId
    ) {
      if (typeof actions?.loadTemplates === "function") {
        await actions.loadTemplates();
      }
      navigate(`/templates/powerpoint-rich/${request.result_data.templateId}/edit`);
      setAnchorEl(null);
      return;
    }

    if (!request.project_id) {
      setAnchorEl(null);
      return;
    }

    if (request.status === "completed" || request.status === "failed") {
      await actions.loadProjects();
    }

    if (request.status === "completed" && request.result_data?.documentId) {
      navigate(
        `/projects/${request.project_id}?tab=documents&highlight=${request.result_data.documentId}`
      );
    } else if (
      request.status === "completed" &&
      request.type === "requirement_extraction"
    ) {
      navigate(`/projects/${request.project_id}?tab=requirements`);
    } else {
      navigate(`/projects/${request.project_id}`);
    }
    setAnchorEl(null);
  };

  /**
   * Whether a request row should be clickable (has a navigation target).
   * @param {object} request - AI request row.
   * @returns {boolean}
   */
  const isNavigable = (request) => {
    if (
      request.type === "template_import" &&
      request.status === "completed" &&
      request.result_data?.templateId
    ) {
      return true;
    }
    return Boolean(request.project_id);
  };

  /**
   * Open the jobs popover from the toolbar button.
   * @param {React.MouseEvent<HTMLElement>} event - Click event.
   * @returns {void}
   */
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Close the jobs popover.
   * @returns {void}
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  const triggerLabel = hasJobs
    ? `AI processing jobs, ${activeCount} active`
    : "AI processing jobs";

  return (
    <>
      <IconButton
        color="inherit"
        id={`${menuId}-trigger`}
        aria-label={triggerLabel}
        aria-hidden={!hasJobs ? true : undefined}
        tabIndex={hasJobs ? 0 : -1}
        aria-haspopup={hasJobs ? "true" : undefined}
        aria-expanded={hasJobs && open ? "true" : "false"}
        aria-controls={
          hasJobs && open ? `${menuId}-popover` : undefined
        }
        onClick={hasJobs ? handleOpen : undefined}
        sx={{
          flexShrink: 0,
          // Idle: keep the slot on wide screens so the top bar does not shift; free it on phones.
          ...(!hasJobs && {
            visibility: "hidden",
            pointerEvents: "none",
            display: { xs: "none", md: "inline-flex" },
          }),
          ...(hasJobs && {
            color: "var(--q-muted, #5E5A54)",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }),
        }}
      >
        {hasJobs ? (
          <Badge
            badgeContent={activeCount}
            color="primary"
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                minWidth: (theme) => theme.spacing(2.25),
              },
            }}
          >
            <PendingActions />
          </Badge>
        ) : (
          <PendingActions />
        )}
      </IconButton>

      <Popover
        id={`${menuId}-popover`}
        open={open && hasJobs}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          "aria-labelledby": `${menuId}-title`,
          elevation: 8,
          sx: {
            width: { xs: "min(100vw - 32px, 380px)", sm: 380 },
            maxHeight: "min(480px, 80vh)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <Typography
            id={`${menuId}-title`}
            component="p"
            variant="subtitle1"
            fontWeight={600}
          >
            Processing
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {activeCount} active, {allRequests.length} total
          </Typography>
        </Box>
        <Box
          component="ul"
          aria-label="AI job list"
          sx={{
            overflow: "auto",
            p: 1,
            flex: 1,
            minHeight: 0,
            listStyle: "none",
            m: 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {allRequests.map((request) => (
            <Box
              key={request.id}
              component="li"
              sx={{
                mb: 1,
                "&:last-of-type": { mb: 0 },
              }}
            >
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    cursor: isNavigable(request) ? "pointer" : "default",
                    transition: "all 0.2s",
                    borderRadius: 1,
                    p: 0.5,
                    m: -0.5,
                    "&:hover": isNavigable(request)
                      ? {
                          bgcolor: "action.hover",
                        }
                      : {},
                  }}
                  role={isNavigable(request) ? "button" : undefined}
                  tabIndex={isNavigable(request) ? 0 : undefined}
                  aria-label={
                    isNavigable(request)
                      ? `Open ${getTypeLabel(request.type)}: ${request.title}`
                      : undefined
                  }
                  onClick={() => {
                    if (isNavigable(request)) {
                      handleNavigateToProject(request);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (
                      isNavigable(request) &&
                      (e.key === "Enter" || e.key === " ")
                    ) {
                      e.preventDefault();
                      handleNavigateToProject(request);
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    {getStatusIcon(request.status)}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {request.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {getTypeLabel(request.type)}
                      </Typography>
                    </Box>
                  </Box>

                  {(request.status === "pending" ||
                    request.status === "processing") && (
                    <Box sx={{ mb: 1 }}>
                      <LinearProgress
                        variant={
                          request.progress ? "determinate" : "indeterminate"
                        }
                        value={request.progress || 0}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      {request.progress_message && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {request.progress_message}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {request.status === "completed" && getCompletedMessage(request) && (
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {getCompletedMessage(request)}
                      {isNavigable(request) ? " — click to open." : ""}
                    </Typography>
                  )}

                  {request.status === "failed" && request.error_message && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mt: 1 }}
                    >
                      Error: {request.error_message}
                    </Typography>
                  )}

                  <Box
                    sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}
                  >
                    <Chip
                      label={request.status}
                      size="small"
                      color={getStatusColor(request.status)}
                      icon={
                        request.status === "completed" ? (
                          <CheckCircle fontSize="small" />
                        ) : undefined
                      }
                    />
                    {request.priority > 0 && (
                      <Chip
                        label={`Priority: ${request.priority}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexShrink: 0,
                    gap: 0.5,
                    pt: 0.25,
                  }}
                >
                  {(request.status === "pending" ||
                    request.status === "processing") && (
                    <Tooltip title="Cancel">
                      <IconButton
                        size="small"
                        aria-label={`Cancel ${request.title}`}
                        onClick={(e) => handleCancel(request.id, e)}
                      >
                        <Cancel fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {(request.status === "completed" ||
                    request.status === "failed") && (
                    <Tooltip title="Dismiss">
                      <IconButton
                        size="small"
                        aria-label={`Dismiss ${request.title}`}
                        onClick={(e) => handleDismiss(request.id, e)}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Paper>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
}

export default AIRequestNotifications;
